import { useCallback, useEffect, useRef, useState } from "react";
import { Route, Plus, Trash2, Pencil, Plane, TrainFront, Bus, Ship, Car, CarTaxiFront, Hotel, HandPlatter, MapPin, Radar, Loader2, Ticket, ScanLine, Sparkles, Paperclip, AlertTriangle, Search, FileUp, TrendingDown, AlarmClock, Users, PersonStanding, Music, ArrowRightCircle, Map, Laptop, StickyNote, ParkingCircle, Utensils, Drama, Flag, Bus as BusIcon, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { googleFlightsUrl } from "../../lib/googleFlights";
import { Modal, photoSrc, inp, lbl } from "../gear/GearBits";
import { DirectionsButton } from "../DirectionsButton";
import DirectionsField from "./DirectionsField";
import { segmentDirectionsQuery } from "../../lib/directions";
import SegmentImport from "./SegmentImport";
import TimelineDayView from "./TimelineDayView";
import LoungeSuggest from "./LoungeSuggest";
import DeadlineReminderDialog from "./DeadlineReminderDialog";

/* Multi-mode trip segments with tickets attached to each leg + chronological timeline.
   Flights support live terminal/gate lookup and boarding-pass OCR (AI extracts — user confirms).
   Feb 2026: added TripIt-parity plan types (Cruise, Concert, Meeting, Meet Someone, Note,
   Parking, Restaurant, Theater, Tour, Directions, Map, Rail, Transportation, Activity,
   Car Rental) and an iOS-native "Add a Plan" grouped picker. */

export const MODES = [
  // ── Most Popular ──
  ["flight",         "Flight",              Plane,           "popular"],
  ["activity",       "Activity",            PersonStanding,  "popular"],
  ["hotel",          "Lodging / Hotel",     Hotel,           "popular"],
  // ── More ──
  ["car_rental",     "Car Rental",          Car,             "more"],
  ["meet_someone",   "Meet Someone",        Users,           "more"],  // NEW plan type ⭐
  ["restaurant",     "Restaurant",          Utensils,        "more"],
  ["concert",        "Concert",             Music,           "more"],
  ["cruise",         "Cruise",              Ship,            "more"],
  ["directions",     "Directions",          ArrowRightCircle,"more"],
  ["ferry",          "Ferry",               Ship,            "more"],
  ["map",            "Map",                 Map,             "more"],
  ["meeting",        "Meeting",             Laptop,          "more"],
  ["note",           "Note",                StickyNote,      "more"],
  ["parking",        "Parking",             ParkingCircle,   "more"],
  ["rail",           "Rail",                TrainFront,      "more"],
  ["theater",        "Theater",             Drama,           "more"],
  ["tour",           "Tour",                Flag,            "more"],
  ["transportation", "Transportation",      BusIcon,         "more"],
  // ── Legacy fallback ──
  ["train",          "Train",               TrainFront,      "legacy"],
  ["bus",            "Bus",                 Bus,             "legacy"],
  ["taxi",           "Taxi / Ride",         CarTaxiFront,    "legacy"],
  ["car",            "Car",                 Car,             "legacy"],
  ["pickup",         "Pickup / Transfer",   MapPin,          "legacy"],
  ["reservation",    "Reservation",         HandPlatter,     "legacy"],
  ["other",          "Other",               Route,           "legacy"],
];
const modeMeta = (m) => MODES.find(([k]) => k === m) || MODES[MODES.length - 1];
const FLIGHT_REF_RE = /^[A-Za-z0-9]{2,3}\s?\d{1,4}[A-Za-z]?$/;

const LABELS = {
  hotel: { from: "Hotel / lodging name", to: "Address", date: "Check-in date", time: "Check-in time", arrive_date: "Check-out date", arrive_time: "Check-out time", ref: "Confirmation #" },
  pickup: { from: "Pickup location", to: "Drop-off / destination", ref: "Booking ref", passenger: "Driver name / phone" },
  taxi: { from: "Pickup address", to: "Destination", ref: "Booking ref", passenger: "Driver / provider" },
  reservation: { from: "Venue / address", to: "", ref: "Confirmation #" },
  flight: { ref: "Flight # (e.g. LH469)", from: "From (airport/city)", to: "To (airport/city)" },
  train: { ref: "Train #", from: "Departure station", to: "Arrival station" },
  rail: { ref: "Train # / route", from: "Departure station", to: "Arrival station" },
  bus: { ref: "Bus # / route", from: "Departure", to: "Arrival" },
  ferry: { ref: "Journey / vessel", from: "Departure port", to: "Arrival port" },
  cruise: { ref: "Booking #", from: "Cruise line / ship", to: "Departure port", date: "Boarding date", time: "Boarding time", arrive_date: "Return date" },
  meet_someone: { from: "Person's name", to: "Where to meet", ref: "Note / topic", date: "Meeting date", time: "Meeting time", arrive_date: "Ends on", arrive_time: "Ends at" },
  meeting: { from: "Meeting title / topic", to: "Where / video link", ref: "Confirmation #", date: "Meeting date", time: "Start time", arrive_time: "End time" },
  restaurant: { from: "Restaurant name", to: "Address", ref: "Reservation #", date: "Reservation date", time: "Reservation time", passenger: "Party name" },
  concert: { from: "Concert / act", to: "Venue", ref: "Confirmation #", date: "Show date", time: "Show time" },
  theater: { from: "Show title", to: "Venue", ref: "Confirmation #", date: "Show date", time: "Show time" },
  tour: { from: "Tour name", to: "Meeting point", ref: "Booking #", date: "Tour date", time: "Start time" },
  activity: { from: "Activity name", to: "Location", ref: "Booking #", date: "Date", time: "Time" },
  car_rental: { from: "Rental company", to: "Pickup location", ref: "Confirmation #", date: "Pickup date", time: "Pickup time", arrive_date: "Return date", arrive_time: "Return time" },
  parking: { from: "Parking facility", to: "Address", ref: "Confirmation #", date: "Park from", time: "Time", arrive_date: "Until" },
  transportation: { from: "From", to: "To", ref: "Confirmation #", date: "Date", time: "Time" },
  directions: { from: "Start", to: "End", ref: "", date: "Date", time: "Time" },
  map: { from: "Location name", to: "Address", ref: "", date: "", time: "" },
  note: { from: "Title", to: "", ref: "", date: "Date", time: "" },
};
const L = (mode, key, fallback) => LABELS[mode]?.[key] ?? fallback;

const fmtTime = (t) => (t ? t.slice(0, 5) : "");

/* iOS-native reusable field — thin underline label above, big input below.
   Matches the TripIt phone-form aesthetic from user's reference screenshots. */
function IosField({ label, value, onChange, placeholder, type = "text", testid, inputMode, required }) {
  return (
    <label className="block">
      <div className={`text-[13px] mb-1 ${required ? "text-blue-600 font-600" : "text-slate-500"}`}>
        {label}{required && "*"}
      </div>
      <input
        data-testid={testid}
        type={type}
        inputMode={inputMode}
        value={value || ""}
        placeholder={placeholder || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent text-slate-900 text-base py-1.5 border-0 border-b border-slate-300 focus:border-blue-500 focus:ring-0 outline-none placeholder:text-slate-300"
      />
    </label>
  );
}

/* Universal "Upload Photo" block — every form gets this per the project standard.
   Accepts images + PDFs, drops on mobile camera, shows preview thumbs.
   iOS Safari fix (iter 195): use a <label> wrapping the <input> so the tap
   triggers the file input natively — ref.current.click() from a button is
   unreliable in Safari and was reported as the "Add photo blue tab not
   clickable" bug. */
function IosPhotoBlock({ tickets, onPick, busy, testid = "form-photo-upload" }) {
  return (
    <div>
      <div className="text-[13px] text-blue-600 font-600 mb-1.5">Upload Photo</div>
      <label
        data-testid={testid}
        className={`w-full rounded-xl border-2 border-dashed border-slate-300 hover:border-blue-400 bg-slate-50 hover:bg-blue-50 transition py-4 text-sm text-slate-600 flex items-center justify-center gap-2 ${busy ? "opacity-50 cursor-wait" : "cursor-pointer"}`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
        {busy ? "Uploading…" : "Attach a photo or document"}
        <input
          type="file"
          accept="image/*,application/pdf"
          className="sr-only"
          onChange={onPick}
          disabled={busy}
        />
      </label>
      {(tickets || []).length > 0 && (
        <div className="mt-2 grid grid-cols-4 gap-2" data-testid={`${testid}-thumbs`}>
          {tickets.map((u, i) => (
            <div key={i} className="aspect-square rounded-lg border border-slate-200 bg-white overflow-hidden grid place-items-center">
              {/\.(pdf)$/i.test(u) ? <FileUp className="w-6 h-6 text-slate-400" /> : <img src={photoSrc(u)} alt="" className="w-full h-full object-cover" />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function TripSegments({ trip, readOnly }) {
  const [segments, setSegments] = useState(trip.segments || []);
  const [editing, setEditing] = useState(null); // segment object or {} for new
  const [deadlineSeg, setDeadlineSeg] = useState(null); // segment for cancellation-reminder dialog
  const [importing, setImporting] = useState(false);
  const [view, setView] = useState("list"); // list | days
  const [gates, setGates] = useState({});
  const [layovers, setLayovers] = useState({}); // segment_id -> warning
  useEffect(() => { setSegments(trip.segments || []); }, [trip.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const indexLayovers = (list) =>
    setLayovers(Object.fromEntries((list || []).map((w) => [w.segment_id, w])));
  useEffect(() => {
    api.get(`/trips/${trip.id}/layovers`).then((r) => indexLayovers(r.data.warnings)).catch(() => {});
  }, [trip.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const persist = useCallback(async (next) => {
    const { data } = await api.put(`/trips/${trip.id}/segments`, { segments: next });
    setSegments(data.segments);
    indexLayovers(data.layovers);
    return data.segments;
  }, [trip.id]);

  const save = async (seg) => {
    const addAnother = seg._add_another;
    if (addAnother) delete seg._add_another;
    const next = seg.id && segments.some((s) => s.id === seg.id)
      ? segments.map((s) => (s.id === seg.id ? seg : s))
      : [...segments, seg];
    try {
      await persist(next);
      if (addAnother && seg.mode === "flight") {
        // Reopen the Flight form pre-set to a blank next-leg, defaulting to the
        // arrival airport of the leg we just saved and same departure date.
        toast.success("Flight saved — add the next leg");
        setEditing({
          mode: "flight",
          from_place: seg.to_place || "",
          date: seg.arrive_date || seg.date || "",
          passenger: seg.passenger || "",
          booking_ref: seg.booking_ref || "",  // same PNR usually
        });
      } else {
        setEditing(null);
        toast.success("Timeline updated");
      }
    }
    catch { toast.error("Could not save segment"); }
  };
  const remove = async (id) => {
    try { await persist(segments.filter((s) => s.id !== id)); } catch { toast.error("Could not delete"); }
  };

  const addImported = async (segs) => {
    try {
      await persist([...segments, ...segs]);
      toast.success(`${segs.length} leg${segs.length > 1 ? "s" : ""} added to the timeline`);
    } catch { toast.error("Could not add the imported legs"); throw new Error("persist failed"); }
  };

  // hotel quick-add destination: last stop, unless it's the round-trip origin → middle stop
  const stops = trip.stops || [];
  const hotelDest = stops.length > 1 && stops[stops.length - 1] === stops[0]
    ? stops[Math.floor(stops.length / 2)] : stops[stops.length - 1];

  const lookupGates = async (seg) => {
    const num = (seg.ref || "").trim();
    if (!FLIGHT_REF_RE.test(num)) return toast.error("Add a flight number first (e.g. LH469)");
    setGates((g) => ({ ...g, [seg.id]: { loading: true } }));
    try {
      const r = await api.get(`/flightsearch/${encodeURIComponent(num.replace(/\s/g, ""))}`);
      const flights = r.data.flights || [];
      setGates((g) => ({ ...g, [seg.id]: { flights, error: flights.length ? null : "No live data found right now." } }));
    } catch (e) {
      setGates((g) => ({ ...g, [seg.id]: { error: e?.response?.data?.detail || "Flight lookup failed" } }));
    }
  };

  if (readOnly && segments.length === 0) return null;

  // chronological grouping by date
  const sorted = [...segments];
  const groups = [];
  sorted.forEach((s) => {
    const d = s.date || "No date yet";
    const g = groups.find((x) => x.date === d);
    if (g) g.items.push(s); else groups.push({ date: d, items: [s] });
  });

  return (
    <section className="mb-10" data-testid="trip-segments">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <h2 className="font-heading font-900 text-xl flex items-center gap-2">
          <Route className="w-5 h-5 text-primary" /> Trip Timeline
        </h2>
        {segments.length > 0 && (
          <div className="flex border border-zinc-300 text-xs font-bold" data-testid="timeline-view-toggle">
            <button onClick={() => setView("list")} data-testid="timeline-view-list"
              className={`px-2.5 py-1 ${view === "list" ? "bg-zinc-800 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}>List</button>
            <button onClick={() => setView("days")} data-testid="timeline-view-days"
              className={`px-2.5 py-1 ${view === "days" ? "bg-zinc-800 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}>Day by day</button>
          </div>
        )}
        {!readOnly && (
          <div className="ml-auto flex flex-wrap gap-2">
            <button onClick={() => setImporting(true)} data-testid="import-legs-btn"
              title="Paste text or upload a screenshot/PDF — AI extracts the legs"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border border-orange-500 text-orange-700 hover:bg-orange-50">
              <FileUp className="w-4 h-4" /> Import legs (AI)
            </button>
            {hotelDest && (
              <button onClick={() => setEditing({ mode: "hotel", from_place: hotelDest, date: trip.start_date || "", arrive_date: trip.end_date || "" })}
                data-testid="add-hotel-stay" title="Save your hotel booking as a leg of this trip"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border border-teal-600 text-teal-700 hover:bg-teal-50">
                <Hotel className="w-4 h-4" /> Add hotel stay
              </button>
            )}
            <button onClick={() => setEditing({ scan: true, mode: "flight" })} data-testid="scan-boarding-pass"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border border-violet-600 text-violet-700 hover:bg-violet-50">
              <ScanLine className="w-4 h-4" /> Scan boarding pass
            </button>
            <button onClick={() => setEditing({})} data-testid="add-segment"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-primary text-white hover:bg-blue-700">
              <Plus className="w-4 h-4" /> Add leg
            </button>
          </div>
        )}
      </div>

      {!readOnly && trip.imported && segments.length > 0 && (
        <div className="border border-sky-200 bg-sky-50 text-sky-800 text-xs p-2.5 mb-3 flex items-start gap-1.5" data-testid="imported-edit-hint">
          <Pencil className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>This trip was imported — tap the <b>pencil</b> on any leg to edit and save it, the trash
            icon to remove it, or <b>Add leg</b> for anything the import missed.</span>
        </div>
      )}

      {segments.length === 0 ? (
        <div className="border border-dashed border-zinc-300 p-8 text-center text-sm text-muted-foreground">
          Build your trip leg by leg — flights, trains, buses, taxis, boats, hotels, pickups and reservations,
          each with tickets attached. Everything appears on one chronological timeline.
        </div>
      ) : view === "days" ? (
        <TimelineDayView trip={trip} segments={segments} readOnly={readOnly} onEdit={(s) => setEditing(s)} />
      ) : (
        <div className="space-y-5">
          {groups.map((g) => (
            <div key={g.date}>
              <div className="font-mono text-xs font-bold uppercase tracking-widest text-amber-700 mb-2">{g.date}</div>
              <div className="border-l-2 border-zinc-200 ml-2 space-y-3">
                {g.items.map((s) => (
                  <SegmentCard key={s.id} seg={s} readOnly={readOnly} gates={gates[s.id]} warning={layovers[s.id]}
                    onEdit={() => setEditing(s)} onDelete={() => remove(s.id)} onGates={() => lookupGates(s)}
                    onDeadline={() => setDeadlineSeg(s)} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing !== null && (
        <SegmentForm seg={editing} onSave={save} onClose={() => setEditing(null)} />
      )}
      {deadlineSeg !== null && (
        <DeadlineReminderDialog seg={deadlineSeg} tripId={trip.id} onClose={() => setDeadlineSeg(null)} />
      )}
      {importing && (
        <SegmentImport trip={trip} onAdd={addImported} onClose={() => setImporting(false)} />
      )}
    </section>
  );
}

function SegmentCard({ seg, readOnly, gates, warning, onEdit, onDelete, onGates, onDeadline }) {
  const [, label, Icon] = modeMeta(seg.mode);
  const chips = [seg.stay_status && `Stay: ${seg.stay_status}`,
    seg.cancel_deadline && `Cancel by ${seg.cancel_deadline.slice(0, 10)}`,
    seg.refund_type && seg.mode === "flight" && `Refund: ${seg.refund_type}`,
    seg.seat && `Seat ${seg.seat}`, seg.terminal && `Terminal ${seg.terminal}`,
    seg.gate && `Gate ${seg.gate}`, seg.booking_ref && `PNR ${seg.booking_ref}`,
    seg.ticket_number && `Ticket ${seg.ticket_number}`, seg.passenger].filter(Boolean);
  const warnStyle = warning && (warning.severity === "warning"
    ? "border-amber-300 bg-amber-50 text-amber-800"
    : "border-red-300 bg-red-50 text-red-700");
  const watchPrice = async () => {
    const iata = (s) => (String(s || "").toUpperCase().match(/\b([A-Z]{3})\b/) || [])[1];
    const origin = iata(seg.from_place), dest = iata(seg.to_place);
    const date = /^\d{4}-\d{2}-\d{2}$/.test(seg.date || "") ? seg.date : "";
    if (!origin || !dest || !date) {
      // Missing IATA code or date — open Price Watch prefilled with what we have
      window.location.assign(`/price-watch?origin=${origin || ""}&dest=${dest || ""}&date=${date}`);
      return;
    }
    try {
      await api.post("/price-watch", { origin, destination: dest, depart_date: date, currency: "USD" });
      toast.success(`Watching fares for ${origin} → ${dest}`, {
        action: { label: "View watches", onClick: () => window.location.assign("/price-watch") },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create price watch");
    }
  };
  return (
    <div className="relative pl-5" data-testid={`segment-${seg.id}`}>
      <span className="absolute -left-[9px] top-3 w-4 h-4 rounded-full bg-white border-2 border-primary flex items-center justify-center" />
      {warning && (
        <div className={`border px-2.5 py-1.5 mb-1 text-xs font-medium ${warnStyle}`}
          data-testid={`layover-warning-${seg.id}`}>
          <div className="flex items-start gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>{warning.message}</span>
          </div>
          {warning.tips?.length > 0 && (
            <details className="mt-1 ml-5" data-testid={`transit-guide-${seg.id}`}>
              <summary className="cursor-pointer font-700 hover:underline">
                Airport transit guide{warning.airport ? ` — ${warning.airport}` : ""}
              </summary>
              <ul className="mt-1 space-y-0.5 list-disc pl-4 font-normal">
                {warning.tips.map((t, i) => <li key={i}>{t}</li>)}
              </ul>
              {warning.airport && (
                <a href={`/airports?q=${encodeURIComponent(warning.airport)}`} target="_blank" rel="noopener noreferrer"
                  className="inline-block mt-1 font-700 underline">Airport info, maps &amp; lounges →</a>
              )}
              <LoungeSuggest airport={warning.airport} />
            </details>
          )}
        </div>
      )}
      <div className="border border-zinc-300 bg-white p-3">
        <div className="flex flex-wrap items-center gap-2">
          <Icon className="w-4 h-4 text-primary shrink-0" />
          <span className="text-xs font-900 uppercase tracking-widest text-zinc-500">{label}</span>
          {seg.ref && <span className="font-mono font-bold text-sm">{seg.ref}</span>}
          {seg.carrier && <span className="text-xs text-muted-foreground">({seg.carrier})</span>}
          <span className="ml-auto font-mono text-sm font-bold">{fmtTime(seg.time)}</span>
          {segmentDirectionsQuery(seg) && (
            <DirectionsButton size="icon" name={segmentDirectionsQuery(seg)} testid={`directions-${seg.id}`} />
          )}
          {!readOnly && (
            <span className="flex gap-0.5">
              {seg.mode === "flight" && <button onClick={onGates} title="Live terminal & gate" data-testid={`gates-${seg.id}`} className="p-1 text-zinc-400 hover:text-emerald-600"><Radar className="w-4 h-4" /></button>}
              {seg.mode === "flight" && seg.from_place && seg.to_place && (
                <a href={googleFlightsUrl({ from: seg.from_place, to: seg.to_place, depart: seg.date || "" })}
                  target="_blank" rel="noopener noreferrer" title="Find this route on Google Flights"
                  data-testid={`google-flights-${seg.id}`} className="p-1 text-zinc-400 hover:text-blue-600">
                  <Search className="w-4 h-4" />
                </a>
              )}
              {seg.mode === "flight" && seg.from_place && seg.to_place && (
                <button onClick={watchPrice} title="Watch fares for this route"
                  data-testid={`watch-price-${seg.id}`} className="p-1 text-zinc-400 hover:text-[#34A853]">
                  <TrendingDown className="w-4 h-4" />
                </button>
              )}
              {["flight", "hotel", "car", "reservation"].includes(seg.mode) && (
                <button onClick={onDeadline} title="Cancellation deadline reminders"
                  data-testid={`deadline-btn-${seg.id}`}
                  className={`p-1 hover:text-teal-600 ${seg.cancel_deadline ? "text-teal-600" : "text-zinc-400"}`}>
                  <AlarmClock className="w-4 h-4" />
                </button>
              )}
              <button onClick={onEdit} data-testid={`edit-segment-${seg.id}`} className="p-1 text-zinc-400 hover:text-primary"><Pencil className="w-4 h-4" /></button>
              <button onClick={onDelete} data-testid={`delete-segment-${seg.id}`} className="p-1 text-zinc-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
            </span>
          )}
        </div>
        <div className="text-sm mt-1 font-medium">
          {seg.from_place}{seg.to_place && <span className="text-zinc-400"> → </span>}{seg.to_place}
          {(seg.arrive_date || seg.arrive_time) && (
            <span className="text-xs text-muted-foreground ml-2">
              {seg.mode === "hotel" ? "until" : "arrives"} {seg.arrive_date || ""} {fmtTime(seg.arrive_time)}
            </span>
          )}
        </div>
        {chips.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {chips.map((c, i) => <span key={i} className="text-[10px] font-bold bg-blue-50 text-blue-700 px-1.5 py-0.5">{c}</span>)}
          </div>
        )}
        {seg.notes && <p className="text-xs text-muted-foreground mt-1.5">{seg.notes}</p>}
        {(seg.tickets || []).length > 0 && (
          <div className="flex gap-2 mt-2">
            {seg.tickets.map((u, i) => (
              <a key={i} href={photoSrc(u)} target="_blank" rel="noreferrer" data-testid={`ticket-thumb-${seg.id}-${i}`}>
                <img src={photoSrc(u)} alt="ticket" className="w-12 h-12 object-cover border border-zinc-300 hover:border-primary" />
              </a>
            ))}
          </div>
        )}
        {gates?.loading && <p className="text-xs text-muted-foreground mt-2 animate-pulse flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Checking live gates…</p>}
        {gates?.error && <p className="text-xs text-red-600 mt-2">{gates.error}</p>}
        {gates?.flights?.map((f, i) => (
          <div key={i} className="mt-2 border border-emerald-200 bg-emerald-50 p-2 text-xs space-y-0.5" data-testid={`live-gates-${seg.id}`}>
            <div className="font-bold text-emerald-800">Live: {f.flight_number || seg.ref} {f.status ? `· ${f.status}` : ""}</div>
            {["departure", "arrival"].map((k) => f[k] && (
              <div key={k}><b>{k === "departure" ? "Dep" : "Arr"}</b> {f[k].iata || ""} · Terminal {f[k].terminal || "—"} · Gate {f[k].gate || "—"}
                {(f[k].estimated_local || f[k].scheduled_local) && <span className="text-zinc-500"> · {(f[k].estimated_local || f[k].scheduled_local).replace("T", " ").slice(0, 16)}</span>}</div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function SegmentForm({ seg, onSave, onClose }) {
  const [f, setF] = useState({ id: seg.id, mode: seg.mode || "flight", ref: "", carrier: "", from_place: "", to_place: "",
    date: "", time: "", arrive_date: "", arrive_time: "", seat: "", terminal: "", gate: "",
    booking_ref: "", ticket_number: "", passenger: "", notes: "", tickets: [], ...seg });
  const [busy, setBusy] = useState(false);
  const [scanBusy, setScanBusy] = useState(false);
  const [scanned, setScanned] = useState(false);
  const fileRef = useRef(null);
  const scanRef = useRef(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const uploadTicket = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/gear/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((p) => ({ ...p, tickets: [...(p.tickets || []), data.url].slice(0, 6) }));
      toast.success("Ticket attached to this leg");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const scanPass = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/gear/ai/boarding-pass", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 });
      const x = data.extracted;
      if (!x) { toast.error("Could not read the boarding pass — fill fields manually"); return; }
      // also attach the pass image itself
      const fd2 = new FormData();
      fd2.append("file", file);
      let url = null;
      try { url = (await api.post("/gear/photos", fd2, { headers: { "Content-Type": "multipart/form-data" } })).data.url; } catch {}
      setF((p) => ({ ...p, mode: "flight",
        ref: x.flight_number || p.ref, carrier: x.airline || p.carrier,
        from_place: x.depart || p.from_place, to_place: x.arrive || p.to_place,
        date: /^\d{4}-\d{2}-\d{2}$/.test(x.date) ? x.date : p.date,
        time: x.depart_time || p.time, seat: x.seat || p.seat, gate: x.gate || p.gate,
        terminal: x.terminal || p.terminal, booking_ref: x.pnr || p.booking_ref,
        passenger: x.passenger || p.passenger,
        notes: [p.notes, x.boarding_time && `Boarding ${x.boarding_time}`, x.boarding_group && `Group ${x.boarding_group}`].filter(Boolean).join(" · "),
        tickets: url ? [...(p.tickets || []), url].slice(0, 6) : p.tickets }));
      setScanned(true);
    } catch { toast.error("Boarding pass scan failed"); }
    finally { setScanBusy(false); }
  };

  const submit = () => {
    if (!(f.ref || f.from_place || f.to_place)) return toast.error("Add at least a number, origin or destination");
    onSave({ ...f, id: f.id });
  };

  const m = f.mode;
  const isMeetSomeone = m === "meet_someone";
  return (
    <Modal title={seg.id ? "Edit plan" : "Add a Plan"} onClose={onClose} wide>
      <div className="space-y-4" data-testid="segment-form">
        {/* iOS-native "Add a Plan" grouped picker (TripIt-parity) */}
        <div className="space-y-3" data-testid="plan-picker">
          <div>
            <div className="text-[11px] font-800 uppercase tracking-widest text-slate-500 mb-1.5 px-1">Most Popular</div>
            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden divide-y divide-slate-100">
              {MODES.filter(([, , , g]) => g === "popular").map(([k, v, Icon]) => (
                <button key={k} type="button" onClick={() => setF({ ...f, mode: k })} data-testid={`mode-${k}`}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition ${m === k ? "bg-blue-50" : "hover:bg-slate-50"}`}>
                  <span className={`w-9 h-9 rounded-full grid place-items-center ${m === k ? "bg-blue-600 text-white" : "bg-blue-500 text-white"} shrink-0`}>
                    <Icon className="w-4.5 h-4.5" />
                  </span>
                  <span className={`text-base ${m === k ? "font-800 text-blue-900" : "text-slate-800"}`}>{v}</span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[11px] font-800 uppercase tracking-widest text-slate-500 mb-1.5 px-1">More</div>
            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden divide-y divide-slate-100 max-h-64 overflow-y-auto">
              {MODES.filter(([, , , g]) => g === "more").map(([k, v, Icon]) => (
                <button key={k} type="button" onClick={() => setF({ ...f, mode: k })} data-testid={`mode-${k}`}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition ${m === k ? "bg-blue-50" : "hover:bg-slate-50"}`}>
                  <span className={`w-9 h-9 rounded-full grid place-items-center ${m === k ? "bg-blue-600 text-white" : "bg-blue-500 text-white"} shrink-0`}>
                    <Icon className="w-4.5 h-4.5" />
                  </span>
                  <span className={`text-base ${m === k ? "font-800 text-blue-900" : "text-slate-800"}`}>{v}</span>
                  {k === "meet_someone" && <span className="ml-auto text-[9px] font-800 uppercase tracking-widest bg-amber-100 text-amber-800 rounded-full px-1.5 py-0.5">NEW</span>}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* iOS-native form panel for the new "Meet Someone" plan type */}
        {isMeetSomeone && (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4" data-testid="form-meet-someone">
            <IosField label="Person's name" value={f.from_place} onChange={(v) => setF({ ...f, from_place: v })} placeholder="Who are you meeting?" testid="mts-name" required />
            <IosPhotoBlock tickets={f.tickets} onPick={uploadTicket} busy={busy} testid="mts-upload" />
            <div className="grid grid-cols-2 gap-4">
              <IosField label="Meeting date" type="date" value={f.date} onChange={(v) => setF({ ...f, date: v })} testid="mts-date" />
              <IosField label="Meeting time" type="time" value={f.time} onChange={(v) => setF({ ...f, time: v })} testid="mts-time" />
            </div>
            <IosField label="Where to meet" value={f.to_place} onChange={(v) => setF({ ...f, to_place: v })} placeholder="Cafe, address or landmark" testid="mts-where" />
            <IosField label="Phone" type="tel" value={f.phone || ""} onChange={(v) => setF({ ...f, phone: v })} placeholder="+1 555 0100" testid="mts-phone" />
            <IosField label="Email" type="email" value={f.email || ""} onChange={(v) => setF({ ...f, email: v })} placeholder="jane@example.com" testid="mts-email" />
            <IosField label="Notes" value={f.notes} onChange={(v) => setF({ ...f, notes: v })} placeholder="Topic, agenda or anything to remember" testid="mts-notes" />
          </div>
        )}

        {/* iOS-native Activity form — per user screenshot */}
        {m === "activity" && (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4" data-testid="form-activity">
            <IosField label="Event Name" value={f.from_place} onChange={(v) => setF({ ...f, from_place: v })} placeholder="Museum visit, hike, concert…" testid="act-name" required />
            <IosPhotoBlock tickets={f.tickets} onPick={uploadTicket} busy={busy} testid="act-upload" />
            <div className="grid grid-cols-2 gap-4">
              <IosField label="Start Date" type="date" value={f.date} onChange={(v) => setF({ ...f, date: v })} testid="act-start-date" />
              <IosField label="Start Time" type="time" value={f.time} onChange={(v) => setF({ ...f, time: v })} testid="act-start-time" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <IosField label="End Date" type="date" value={f.arrive_date} onChange={(v) => setF({ ...f, arrive_date: v })} testid="act-end-date" />
              <IosField label="End Time" type="time" value={f.arrive_time} onChange={(v) => setF({ ...f, arrive_time: v })} testid="act-end-time" />
            </div>
            <IosField label="Venue" value={f.carrier || ""} onChange={(v) => setF({ ...f, carrier: v })} placeholder="Name of the venue" testid="act-venue" />
            <IosField label="Address" value={f.to_place || ""} onChange={(v) => setF({ ...f, to_place: v })} placeholder="Street address" testid="act-address" />
            <IosField label="Phone" type="tel" value={f.phone || ""} onChange={(v) => setF({ ...f, phone: v })} testid="act-phone" />
            <IosField label="Website" type="url" value={f.website || ""} onChange={(v) => setF({ ...f, website: v })} placeholder="https://…" testid="act-website" />
            <IosField label="Email" type="email" value={f.email || ""} onChange={(v) => setF({ ...f, email: v })} testid="act-email" />
            <IosField label="Confirmation" value={f.ref || ""} onChange={(v) => setF({ ...f, ref: v })} testid="act-confirmation" />
            <IosField label="Total Cost" type="text" inputMode="decimal" value={f.total_cost || ""} onChange={(v) => setF({ ...f, total_cost: v })} placeholder="0.00" testid="act-cost" />
          </div>
        )}

        {/* iOS-native Lodging (Hotel/Resort) form — per user screenshot */}
        {m === "hotel" && (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4" data-testid="form-lodging">
            <IosField label="Lodging Name" value={f.from_place} onChange={(v) => setF({ ...f, from_place: v })} placeholder="Hotel or resort name" testid="lodging-name" required />
            <IosPhotoBlock tickets={f.tickets} onPick={uploadTicket} busy={busy} testid="lodging-upload" />
            <div className="grid grid-cols-2 gap-4">
              <IosField label="Check In Date" type="date" value={f.date} onChange={(v) => setF({ ...f, date: v })} testid="lodging-checkin-date" />
              <IosField label="Check In Time" type="time" value={f.time} onChange={(v) => setF({ ...f, time: v })} testid="lodging-checkin-time" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <IosField label="Checkout Date" type="date" value={f.arrive_date} onChange={(v) => setF({ ...f, arrive_date: v })} testid="lodging-checkout-date" />
              <IosField label="Checkout Time" type="time" value={f.arrive_time} onChange={(v) => setF({ ...f, arrive_time: v })} testid="lodging-checkout-time" />
            </div>
            <IosField label="Address" value={f.to_place || ""} onChange={(v) => setF({ ...f, to_place: v })} placeholder="Street address" testid="lodging-address" />
            <IosField label="Phone" type="tel" value={f.phone || ""} onChange={(v) => setF({ ...f, phone: v })} testid="lodging-phone" />
            <IosField label="Website" type="url" value={f.website || ""} onChange={(v) => setF({ ...f, website: v })} placeholder="https://…" testid="lodging-website" />
            <IosField label="Email" type="email" value={f.email || ""} onChange={(v) => setF({ ...f, email: v })} testid="lodging-email" />
            <IosField label="Confirmation" value={f.ref || ""} onChange={(v) => setF({ ...f, ref: v })} testid="lodging-confirmation" />
            <IosField label="Total Cost" type="text" inputMode="decimal" value={f.total_cost || ""} onChange={(v) => setF({ ...f, total_cost: v })} placeholder="0.00" testid="lodging-cost" />
            <label className="flex items-center justify-between py-2 border-b border-slate-200">
              <span className="text-base text-slate-900">Reservation Purchased</span>
              <input type="checkbox" data-testid="lodging-purchased"
                checked={!!f.reservation_purchased}
                onChange={(e) => setF({ ...f, reservation_purchased: e.target.checked })}
                className="w-11 h-6 rounded-full appearance-none bg-slate-300 checked:bg-emerald-500 relative cursor-pointer transition-colors before:absolute before:content-[''] before:w-5 before:h-5 before:rounded-full before:bg-white before:top-0.5 before:left-0.5 before:transition-transform checked:before:translate-x-5" />
            </label>
          </div>
        )}

        {/* iOS-native Flight form — supports multiple legs (each is its own saved segment) */}
        {m === "flight" && !seg.scan && (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4" data-testid="form-flight">
            <div className="flex items-center justify-between pb-2 border-b border-slate-100">
              <div>
                <div className="font-serif font-800 text-lg text-slate-900">Flight <span className="text-xs text-rose-600 font-600 align-top">Details</span></div>
                <div className="text-[11px] text-slate-500 mt-0.5">Add each flight as its own leg — they'll all show under this trip's flights.</div>
              </div>
            </div>
            <IosPhotoBlock tickets={f.tickets} onPick={uploadTicket} busy={busy} testid="flight-upload" />
            <IosField label="Departure Date" type="date" value={f.date} onChange={(v) => setF({ ...f, date: v })} testid="flight-depart-date" required />
            <div className="grid grid-cols-2 gap-4">
              <IosField label="Departure Time" type="time" value={f.time} onChange={(v) => setF({ ...f, time: v })} testid="flight-depart-time" />
              <IosField label="Arrival Time" type="time" value={f.arrive_time} onChange={(v) => setF({ ...f, arrive_time: v })} testid="flight-arrive-time" />
            </div>
            <IosField label="Airline" value={f.carrier || ""} onChange={(v) => setF({ ...f, carrier: v })} placeholder="Lufthansa" testid="flight-airline" required />
            <IosField label="Flight Number" value={f.ref || ""} onChange={(v) => setF({ ...f, ref: v })} placeholder="LH469" testid="flight-number" required />
            <div className="grid grid-cols-2 gap-4">
              <IosField label="From" value={f.from_place || ""} onChange={(v) => setF({ ...f, from_place: v })} placeholder="PDX" testid="flight-from" />
              <IosField label="To" value={f.to_place || ""} onChange={(v) => setF({ ...f, to_place: v })} placeholder="FRA" testid="flight-to" />
            </div>
            <IosField label="Confirmation" value={f.booking_ref || ""} onChange={(v) => setF({ ...f, booking_ref: v })} placeholder="PNR" testid="flight-confirmation" />
            <IosField label="Seat" value={f.seat || ""} onChange={(v) => setF({ ...f, seat: v })} placeholder="12A" testid="flight-seat" />
            <div className="text-[11px] text-slate-400 text-center pt-1">
              Have a connecting flight? Save this one, then tap <b className="text-slate-600">Add a Plan → Flight</b> again for the next leg.
            </div>
          </div>
        )}

        {(m === "flight" || seg.scan) && (
          <div className="border border-violet-200 bg-violet-50 p-3">
            <button type="button" onClick={() => scanRef.current?.click()} disabled={scanBusy} data-testid="scan-pass-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50">
              {scanBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanLine className="w-4 h-4" />}
              {scanBusy ? "Reading boarding pass…" : "Scan boarding pass (AI fills the fields)"}
            </button>
            <input ref={scanRef} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" className="hidden" onChange={scanPass} />
            {scanned && (
              <p className="text-xs text-violet-800 mt-2 flex items-center gap-1" data-testid="scan-verify-note">
                <Sparkles className="w-3.5 h-3.5" /> AI extracted this information — <strong>please verify it against your boarding pass before saving.</strong>
              </p>
            )}
          </div>
        )}

        {/* iOS-native Restaurant form — Breakfast/Lunch/Dinner single-select per user screenshot */}
        {m === "restaurant" && (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4" data-testid="form-restaurant">
            <IosField label="Restaurant" value={f.from_place} onChange={(v) => setF({ ...f, from_place: v })} placeholder="Restaurant name" testid="rest-name" required />
            <div>
              <div className="text-[13px] text-slate-500 mb-2">Meal</div>
              <div className="grid grid-cols-3 gap-2" data-testid="rest-meal-toggle">
                {[["breakfast", "Breakfast"], ["lunch", "Lunch"], ["dinner", "Dinner"]].map(([k, l]) => (
                  <button key={k} type="button"
                    onClick={() => setF({ ...f, meal: f.meal === k ? "" : k })}
                    data-testid={`rest-meal-${k}`}
                    className={`py-2 rounded-lg border text-sm font-700 transition ${f.meal === k
                      ? "border-blue-500 bg-blue-50 text-blue-700"
                      : "border-slate-300 bg-white text-slate-700 hover:border-blue-400"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <IosPhotoBlock tickets={f.tickets} onPick={uploadTicket} busy={busy} testid="rest-upload" />
            <div className="grid grid-cols-2 gap-4">
              <IosField label="Date" type="date" value={f.date} onChange={(v) => setF({ ...f, date: v })} testid="rest-date" />
              <IosField label="Time" type="time" value={f.time} onChange={(v) => setF({ ...f, time: v })} testid="rest-time" />
            </div>
            <IosField label="Address" value={f.to_place || ""} onChange={(v) => setF({ ...f, to_place: v })} placeholder="Street address" testid="rest-address" />
            <IosField label="Phone" type="tel" value={f.phone || ""} onChange={(v) => setF({ ...f, phone: v })} testid="rest-phone" />
            <IosField label="Website" type="url" value={f.website || ""} onChange={(v) => setF({ ...f, website: v })} placeholder="https://…" testid="rest-website" />
            <IosField label="Email" type="email" value={f.email || ""} onChange={(v) => setF({ ...f, email: v })} testid="rest-email" />
            <IosField label="Confirmation" value={f.ref || ""} onChange={(v) => setF({ ...f, ref: v })} placeholder="Reservation #" testid="rest-confirmation" />
            <IosField label="Party name" value={f.passenger || ""} onChange={(v) => setF({ ...f, passenger: v })} placeholder="Name under reservation" testid="rest-party" />
            <IosField label="Total Cost" type="text" inputMode="decimal" value={f.total_cost || ""} onChange={(v) => setF({ ...f, total_cost: v })} placeholder="0.00" testid="rest-cost" />
          </div>
        )}

        {/* iOS-native Car Rental form — sectioned Pickup / Drop-off / Contact / Reservation per user screenshot */}
        {m === "car_rental" && (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-5" data-testid="form-car-rental">
            <IosField label="Rental Agency" value={f.carrier || ""} onChange={(v) => setF({ ...f, carrier: v })} placeholder="Hertz, Enterprise, Avis…" testid="cr-agency" required />
            <IosPhotoBlock tickets={f.tickets} onPick={uploadTicket} busy={busy} testid="cr-upload" />
            <div className="grid grid-cols-2 gap-4">
              <IosField label="Pickup Date" type="date" value={f.date} onChange={(v) => setF({ ...f, date: v })} testid="cr-pick-date" />
              <IosField label="Pickup Time" type="time" value={f.time} onChange={(v) => setF({ ...f, time: v })} testid="cr-pick-time" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <IosField label="Drop-off Date" type="date" value={f.arrive_date} onChange={(v) => setF({ ...f, arrive_date: v })} testid="cr-drop-date" />
              <IosField label="Drop-off Time" type="time" value={f.arrive_time} onChange={(v) => setF({ ...f, arrive_time: v })} testid="cr-drop-time" />
            </div>

            {/* Pickup Location section */}
            <div className="pt-1">
              <div className="font-serif font-800 text-lg text-slate-900 mb-2">Pickup Location</div>
              <div className="space-y-4">
                <IosField label="Pickup Location" value={f.from_place || ""} onChange={(v) => setF({ ...f, from_place: v })} placeholder="Airport counter, city office…" testid="cr-pick-loc" />
                <IosField label="Address" value={f.pickup_address || ""} onChange={(v) => setF({ ...f, pickup_address: v })} placeholder="Street address" testid="cr-pick-addr" />
                <IosField label="Phone" type="tel" value={f.pickup_phone || ""} onChange={(v) => setF({ ...f, pickup_phone: v })} testid="cr-pick-phone" />
              </div>
            </div>

            {/* Drop-Off Location section w/ "Same as pickup" toggle */}
            <div className="pt-1">
              <div className="font-serif font-800 text-lg text-slate-900 mb-2">Drop-Off Location</div>
              <label className="flex items-center justify-between py-2 border-b border-slate-200 mb-3">
                <span className="text-base text-slate-900">Same as Pickup Location</span>
                <input type="checkbox" data-testid="cr-same-toggle"
                  checked={f.drop_same !== false}
                  onChange={(e) => setF({ ...f, drop_same: e.target.checked })}
                  className="w-11 h-6 rounded-full appearance-none bg-slate-300 checked:bg-blue-500 relative cursor-pointer transition-colors before:absolute before:content-[''] before:w-5 before:h-5 before:rounded-full before:bg-white before:top-0.5 before:left-0.5 before:transition-transform checked:before:translate-x-5" />
              </label>
              {f.drop_same === false && (
                <div className="space-y-4" data-testid="cr-drop-fields">
                  <IosField label="Drop-off Location" value={f.to_place || ""} onChange={(v) => setF({ ...f, to_place: v })} placeholder="Different agency / airport" testid="cr-drop-loc" />
                  <IosField label="Address" value={f.drop_address || ""} onChange={(v) => setF({ ...f, drop_address: v })} placeholder="Street address" testid="cr-drop-addr" />
                  <IosField label="Phone" type="tel" value={f.drop_phone || ""} onChange={(v) => setF({ ...f, drop_phone: v })} testid="cr-drop-phone" />
                </div>
              )}
            </div>

            {/* Directions & Time to Destination — auto-derives when both addresses are set */}
            <DirectionsField
              startAddress={f.pickup_address}
              destAddress={(f.drop_same === false ? f.drop_address : f.pickup_address) || ""}
              provider={f.map_provider} averageTime={f.average_time} routeLink={f.route_link}
              onChange={(d) => setF({ ...f, map_provider: d.provider, average_time: d.average_time, route_link: d.route_link })}
              testPrefix="cr-directions" />

            {/* Contact Information section */}
            <div className="pt-1">
              <div className="font-serif font-800 text-lg text-slate-900 mb-2">Contact Information</div>
              <div className="space-y-4">
                <IosField label="Website" type="url" value={f.website || ""} onChange={(v) => setF({ ...f, website: v })} placeholder="https://…" testid="cr-website" />
                <IosField label="Email" type="email" value={f.email || ""} onChange={(v) => setF({ ...f, email: v })} testid="cr-email" />
              </div>
            </div>

            {/* Reservation Details section */}
            <div className="pt-1">
              <div className="font-serif font-800 text-lg text-slate-900 mb-2">Reservation Details</div>
              <div className="space-y-4">
                <IosField label="Confirmation" value={f.ref || ""} onChange={(v) => setF({ ...f, ref: v })} placeholder="Reservation / confirmation #" testid="cr-confirmation" />
                <IosField label="Driver Name" value={f.passenger || ""} onChange={(v) => setF({ ...f, passenger: v })} placeholder="Primary driver" testid="cr-driver" />
                <IosField label="Vehicle" value={f.vehicle || ""} onChange={(v) => setF({ ...f, vehicle: v })} placeholder="SUV, compact, Tesla Model 3…" testid="cr-vehicle" />
                <IosField label="Total Cost" type="text" inputMode="decimal" value={f.total_cost || ""} onChange={(v) => setF({ ...f, total_cost: v })} placeholder="0.00" testid="cr-cost" />
              </div>
            </div>
          </div>
        )}

        {/* iOS-native "Event-style" form — Concert / Meeting / Theater (identical layout, different subtitle).
            Event Name → Upload Photo → Start/End date+time → Venue → Address → Phone → Website → Email → Confirmation → Total Cost */}
        {(m === "concert" || m === "meeting" || m === "theater") && (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4" data-testid={`form-${m}`}>
            {m === "theater" && (
              <div className="text-[10px] font-800 uppercase tracking-widest text-rose-600 -mb-2">Theater · Movie</div>
            )}
            <IosField label="Event Name" value={f.from_place} onChange={(v) => setF({ ...f, from_place: v })}
              placeholder={m === "concert" ? "Concert / performer"
                : m === "theater" ? "Show / movie title"
                : "Meeting title / topic"}
              testid={`${m}-name`} required />
            <IosPhotoBlock tickets={f.tickets} onPick={uploadTicket} busy={busy} testid={`${m}-upload`} />
            <div className="grid grid-cols-2 gap-4">
              <IosField label="Start Date" type="date" value={f.date} onChange={(v) => setF({ ...f, date: v })} testid={`${m}-start-date`} />
              <IosField label="Start Time" type="time" value={f.time} onChange={(v) => setF({ ...f, time: v })} testid={`${m}-start-time`} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <IosField label="End Date" type="date" value={f.arrive_date} onChange={(v) => setF({ ...f, arrive_date: v })} testid={`${m}-end-date`} />
              <IosField label="End Time" type="time" value={f.arrive_time} onChange={(v) => setF({ ...f, arrive_time: v })} testid={`${m}-end-time`} />
            </div>
            <IosField label="Venue" value={f.carrier || ""} onChange={(v) => setF({ ...f, carrier: v })}
              placeholder={m === "concert" ? "Venue name"
                : m === "theater" ? "Theater / cinema"
                : "Room / video link"} testid={`${m}-venue`} />
            <IosField label="Address" value={f.to_place || ""} onChange={(v) => setF({ ...f, to_place: v })} placeholder="Street address" testid={`${m}-address`} />
            <IosField label="Phone" type="tel" value={f.phone || ""} onChange={(v) => setF({ ...f, phone: v })} testid={`${m}-phone`} />
            <IosField label="Website" type="url" value={f.website || ""} onChange={(v) => setF({ ...f, website: v })} placeholder="https://…" testid={`${m}-website`} />
            <IosField label="Email" type="email" value={f.email || ""} onChange={(v) => setF({ ...f, email: v })} testid={`${m}-email`} />
            <IosField label="Confirmation" value={f.ref || ""} onChange={(v) => setF({ ...f, ref: v })} testid={`${m}-confirmation`} />
            <IosField label="Total Cost" type="text" inputMode="decimal" value={f.total_cost || ""} onChange={(v) => setF({ ...f, total_cost: v })} placeholder="0.00" testid={`${m}-cost`} />
          </div>
        )}

        {/* iOS-native Note / Reminder form — includes optional contact person + photo upload */}
        {m === "note" && (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4" data-testid="form-note">
            <div className="text-[10px] font-800 uppercase tracking-widest text-rose-600 -mb-2">Note · Reminder</div>
            <IosField label="Note Title" value={f.from_place} onChange={(v) => setF({ ...f, from_place: v })} placeholder="Short title" testid="note-title" required />
            <IosPhotoBlock tickets={f.tickets} onPick={uploadTicket} busy={busy} testid="note-upload" />
            <div className="grid grid-cols-2 gap-4">
              <IosField label="Date" type="date" value={f.date} onChange={(v) => setF({ ...f, date: v })} testid="note-date" />
              <IosField label="Time" type="time" value={f.time} onChange={(v) => setF({ ...f, time: v })} testid="note-time" />
            </div>
            <IosField label="Address" value={f.to_place || ""} onChange={(v) => setF({ ...f, to_place: v })} placeholder="Optional location" testid="note-address" />
            <IosField label="Website" type="url" value={f.website || ""} onChange={(v) => setF({ ...f, website: v })} placeholder="https://…" testid="note-website" />
            <IosField label="Note" value={f.notes || ""} onChange={(v) => setF({ ...f, notes: v })} placeholder="What is this reminder about?" testid="note-body" />

            <div className="pt-1">
              <div className="font-serif font-800 text-lg text-slate-900 mb-2">Add a person</div>
              <div className="space-y-4">
                <IosField label="Name" value={f.passenger || ""} onChange={(v) => setF({ ...f, passenger: v })} placeholder="Who is this about?" testid="note-person-name" />
                <IosField label="Phone number" type="tel" value={f.phone || ""} onChange={(v) => setF({ ...f, phone: v })} placeholder="+1 555 0100" testid="note-person-phone" />
              </div>
            </div>
          </div>
        )}

        {/* iOS-native Parking form — Street vs Structure single-select + alert toggle + upload */}
        {m === "parking" && (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4" data-testid="form-parking">
            <IosField label="Parking Name" value={f.from_place} onChange={(v) => setF({ ...f, from_place: v })} placeholder="Lot / garage name" testid="park-name" required />
            <IosPhotoBlock tickets={f.tickets} onPick={uploadTicket} busy={busy} testid="park-upload" />
            <div className="grid grid-cols-2 gap-4">
              <IosField label="Drop-off Date" type="date" value={f.date} onChange={(v) => setF({ ...f, date: v })} testid="park-drop-date" />
              <IosField label="Drop-off Time" type="time" value={f.time} onChange={(v) => setF({ ...f, time: v })} testid="park-drop-time" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <IosField label="Pickup Date" type="date" value={f.arrive_date} onChange={(v) => setF({ ...f, arrive_date: v })} testid="park-pick-date" />
              <IosField label="Pickup Time" type="time" value={f.arrive_time} onChange={(v) => setF({ ...f, arrive_time: v })} testid="park-pick-time" />
            </div>
            <div>
              <div className="text-[13px] text-slate-500 mb-2">Type</div>
              <div className="grid grid-cols-2 gap-2" data-testid="park-type-toggle">
                {[["street", "Street"], ["structure", "Parking Structure / Lot"]].map(([k, l]) => (
                  <button key={k} type="button"
                    onClick={() => setF({ ...f, park_type: f.park_type === k ? "" : k })}
                    data-testid={`park-type-${k}`}
                    className={`py-2 rounded-lg border text-sm font-700 transition ${f.park_type === k
                      ? "border-blue-500 bg-blue-50 text-blue-700"
                      : "border-slate-300 bg-white text-slate-700 hover:border-blue-400"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <IosField label="Location / Zone #" value={f.zone || ""} onChange={(v) => setF({ ...f, zone: v })} placeholder="Zone / stall / floor" testid="park-zone" />
            <label className="flex items-center justify-between py-2 border-b border-slate-200">
              <div>
                <div className="text-base text-slate-900">Build alert</div>
                <div className="text-xs text-slate-500">Reminder before pickup time</div>
              </div>
              <input type="checkbox" data-testid="park-alert-toggle"
                checked={!!f.build_alert}
                onChange={(e) => setF({ ...f, build_alert: e.target.checked })}
                className="w-11 h-6 rounded-full appearance-none bg-slate-300 checked:bg-blue-500 relative cursor-pointer transition-colors before:absolute before:content-[''] before:w-5 before:h-5 before:rounded-full before:bg-white before:top-0.5 before:left-0.5 before:transition-transform checked:before:translate-x-5" />
            </label>
            <IosField label="Address" value={f.to_place || ""} onChange={(v) => setF({ ...f, to_place: v })} placeholder="Street address" testid="park-address" />
            <IosField label="Phone" type="tel" value={f.phone || ""} onChange={(v) => setF({ ...f, phone: v })} testid="park-phone" />
            <IosField label="Website" type="url" value={f.website || ""} onChange={(v) => setF({ ...f, website: v })} placeholder="https://…" testid="park-website" />
            <IosField label="Email" type="email" value={f.email || ""} onChange={(v) => setF({ ...f, email: v })} testid="park-email" />
            <IosField label="Confirmation" value={f.ref || ""} onChange={(v) => setF({ ...f, ref: v })} testid="park-confirmation" />
            <IosField label="Total Cost" type="text" inputMode="decimal" value={f.total_cost || ""} onChange={(v) => setF({ ...f, total_cost: v })} placeholder="0.00" testid="park-cost" />
          </div>
        )}

        {/* iOS-native Rail / Metro form — sectioned Departure Station / Arrival Station / Train and Service per user screenshot */}
        {m === "rail" && (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-5" data-testid="form-rail">
            <div className="text-[10px] font-800 uppercase tracking-widest text-rose-600 -mb-2">Rail · Metro</div>
            <IosField label="Carrier Name" value={f.carrier} onChange={(v) => setF({ ...f, carrier: v })} placeholder="Amtrak, Eurostar, MTA…" testid="rail-carrier" required />
            <IosPhotoBlock tickets={f.tickets} onPick={uploadTicket} busy={busy} testid="rail-upload" />
            <div className="grid grid-cols-2 gap-4">
              <IosField label="Departure Date" type="date" value={f.date} onChange={(v) => setF({ ...f, date: v })} testid="rail-dep-date" />
              <IosField label="Departure Time" type="time" value={f.time} onChange={(v) => setF({ ...f, time: v })} testid="rail-dep-time" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <IosField label="Arrival Date" type="date" value={f.arrive_date} onChange={(v) => setF({ ...f, arrive_date: v })} testid="rail-arr-date" />
              <IosField label="Arrival Time" type="time" value={f.arrive_time} onChange={(v) => setF({ ...f, arrive_time: v })} testid="rail-arr-time" />
            </div>
            <IosField label="Confirmation" value={f.ref || ""} onChange={(v) => setF({ ...f, ref: v })} testid="rail-confirmation" />
            <IosField label="Total Cost" type="text" inputMode="decimal" value={f.total_cost || ""} onChange={(v) => setF({ ...f, total_cost: v })} placeholder="0.00" testid="rail-cost" />

            <div className="pt-1">
              <div className="font-serif font-800 text-lg text-slate-900 mb-2">Departure Station</div>
              <div className="space-y-4">
                <IosField label="Departure Station" value={f.from_place || ""} onChange={(v) => setF({ ...f, from_place: v })} placeholder="Station name" testid="rail-dep-station" />
                <IosField label="Departure Address" value={f.start_address || ""} onChange={(v) => setF({ ...f, start_address: v })} placeholder="Street address" testid="rail-dep-addr" />
              </div>
            </div>

            <div className="pt-1">
              <div className="font-serif font-800 text-lg text-slate-900 mb-2">Arrival Station</div>
              <div className="space-y-4">
                <IosField label="Arrival Station" value={f.to_place || ""} onChange={(v) => setF({ ...f, to_place: v })} placeholder="Station name" testid="rail-arr-station" />
                <IosField label="Arrival Address" value={f.end_address || ""} onChange={(v) => setF({ ...f, end_address: v })} placeholder="Street address" testid="rail-arr-addr" />
              </div>
            </div>

            <DirectionsField
              startAddress={f.start_address} destAddress={f.end_address}
              provider={f.map_provider} averageTime={f.average_time} routeLink={f.route_link}
              onChange={(d) => setF({ ...f, map_provider: d.provider, average_time: d.average_time, route_link: d.route_link })}
              testPrefix="rail-directions" />

            <div className="pt-1">
              <div className="font-serif font-800 text-lg text-slate-900 mb-2">Train and Service</div>
              <div className="space-y-4">
                <IosField label="Train Type" value={f.train_type || ""} onChange={(v) => setF({ ...f, train_type: v })} placeholder="ICE, Eurostar, Local…" testid="rail-train-type" />
                <IosField label="Train / Line Number" value={f.train_number || ""} onChange={(v) => setF({ ...f, train_number: v })} placeholder="e.g. 9052" testid="rail-train-num" />
                <IosField label="Coach / Seat" value={f.seat || ""} onChange={(v) => setF({ ...f, seat: v })} placeholder="Coach 12 · Seat 24A" testid="rail-seat" />
                <IosField label="Passenger(s)" value={f.passenger || ""} onChange={(v) => setF({ ...f, passenger: v })} placeholder="Names on ticket" testid="rail-passenger" />
              </div>
            </div>
          </div>
        )}

        {/* iOS-native Tour form — Event-style with /Trip subtitle */}
        {m === "tour" && (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4" data-testid="form-tour">
            <div className="text-[10px] font-800 uppercase tracking-widest text-rose-600 -mb-2">Tour · Trip</div>
            <IosField label="Event Name" value={f.from_place} onChange={(v) => setF({ ...f, from_place: v })} placeholder="Tour name" testid="tour-name" required />
            <IosPhotoBlock tickets={f.tickets} onPick={uploadTicket} busy={busy} testid="tour-upload" />
            <div className="grid grid-cols-2 gap-4">
              <IosField label="Start Date" type="date" value={f.date} onChange={(v) => setF({ ...f, date: v })} testid="tour-start-date" />
              <IosField label="Start Time" type="time" value={f.time} onChange={(v) => setF({ ...f, time: v })} testid="tour-start-time" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <IosField label="End Date" type="date" value={f.arrive_date} onChange={(v) => setF({ ...f, arrive_date: v })} testid="tour-end-date" />
              <IosField label="End Time" type="time" value={f.arrive_time} onChange={(v) => setF({ ...f, arrive_time: v })} testid="tour-end-time" />
            </div>
            <IosField label="Venue" value={f.carrier || ""} onChange={(v) => setF({ ...f, carrier: v })} placeholder="Tour operator / meeting point" testid="tour-venue" />
            <IosField label="Address" value={f.to_place || ""} onChange={(v) => setF({ ...f, to_place: v })} placeholder="Street address" testid="tour-address" />
            <IosField label="Phone" type="tel" value={f.phone || ""} onChange={(v) => setF({ ...f, phone: v })} testid="tour-phone" />
            <IosField label="Website" type="url" value={f.website || ""} onChange={(v) => setF({ ...f, website: v })} placeholder="https://…" testid="tour-website" />
            <IosField label="Email" type="email" value={f.email || ""} onChange={(v) => setF({ ...f, email: v })} testid="tour-email" />
            <IosField label="Confirmation" value={f.ref || ""} onChange={(v) => setF({ ...f, ref: v })} testid="tour-confirmation" />
            <IosField label="Total Cost" type="text" inputMode="decimal" value={f.total_cost || ""} onChange={(v) => setF({ ...f, total_cost: v })} placeholder="0.00" testid="tour-cost" />
          </div>
        )}

        {/* iOS-native Transportation form — sectioned Departure / Arrival / Vehicle Info per user screenshot */}
        {m === "transportation" && (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-5" data-testid="form-transportation">
            <IosField label="Carrier Name" value={f.carrier} onChange={(v) => setF({ ...f, carrier: v })} placeholder="Uber, Lyft, private car…" testid="tx-carrier" required />
            <IosPhotoBlock tickets={f.tickets} onPick={uploadTicket} busy={busy} testid="tx-upload" />
            <div className="grid grid-cols-2 gap-4">
              <IosField label="Departure Date" type="date" value={f.date} onChange={(v) => setF({ ...f, date: v })} testid="tx-dep-date" />
              <IosField label="Departure Time" type="time" value={f.time} onChange={(v) => setF({ ...f, time: v })} testid="tx-dep-time" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <IosField label="Arrival Date" type="date" value={f.arrive_date} onChange={(v) => setF({ ...f, arrive_date: v })} testid="tx-arr-date" />
              <IosField label="Arrival Time" type="time" value={f.arrive_time} onChange={(v) => setF({ ...f, arrive_time: v })} testid="tx-arr-time" />
            </div>
            <IosField label="Confirmation" value={f.ref || ""} onChange={(v) => setF({ ...f, ref: v })} testid="tx-confirmation" />
            <IosField label="Total Cost" type="text" inputMode="decimal" value={f.total_cost || ""} onChange={(v) => setF({ ...f, total_cost: v })} placeholder="0.00" testid="tx-cost" />

            <div className="pt-1">
              <div className="font-serif font-800 text-lg text-slate-900 mb-2">Departure Location</div>
              <div className="space-y-4">
                <IosField label="Departure Location" value={f.from_place || ""} onChange={(v) => setF({ ...f, from_place: v })} placeholder="Pickup point" testid="tx-dep-loc" />
                <IosField label="Departure Address" value={f.start_address || ""} onChange={(v) => setF({ ...f, start_address: v })} placeholder="Street address" testid="tx-dep-addr" />
              </div>
            </div>

            <div className="pt-1">
              <div className="font-serif font-800 text-lg text-slate-900 mb-2">Arrival Location</div>
              <div className="space-y-4">
                <IosField label="Arrival Location" value={f.to_place || ""} onChange={(v) => setF({ ...f, to_place: v })} placeholder="Drop-off point" testid="tx-arr-loc" />
                <IosField label="Arrival Address" value={f.end_address || ""} onChange={(v) => setF({ ...f, end_address: v })} placeholder="Street address" testid="tx-arr-addr" />
              </div>
            </div>

            <div className="pt-1">
              <div className="font-serif font-800 text-lg text-slate-900 mb-2">Vehicle Info</div>
              <div className="space-y-4">
                <IosField label="Vehicle Description" value={f.vehicle || ""} onChange={(v) => setF({ ...f, vehicle: v })} placeholder="Make · model · color" testid="tx-vehicle" />
                <IosField label="Driver / Passenger Name" value={f.passenger || ""} onChange={(v) => setF({ ...f, passenger: v })} testid="tx-driver" />
              </div>
            </div>

            <DirectionsField
              startAddress={f.start_address} destAddress={f.end_address}
              provider={f.map_provider} averageTime={f.average_time} routeLink={f.route_link}
              onChange={(d) => setF({ ...f, map_provider: d.provider, average_time: d.average_time, route_link: d.route_link })}
              testPrefix="tx-directions" />
          </div>
        )}

        {/* iOS-native Cruise form — sectioned Starting Port / Ending Port / Reservation Info per user screenshot */}
        {m === "cruise" && (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-5" data-testid="form-cruise">
            <IosField label="Cruise Line" value={f.carrier || ""} onChange={(v) => setF({ ...f, carrier: v })} placeholder="Royal Caribbean, Carnival…" testid="cruise-line" required />
            <IosField label="Ship Name" value={f.ship || ""} onChange={(v) => setF({ ...f, ship: v })} placeholder="Wonder of the Seas" testid="cruise-ship" />
            <IosPhotoBlock tickets={f.tickets} onPick={uploadTicket} busy={busy} testid="cruise-upload" />
            <div className="grid grid-cols-2 gap-4">
              <IosField label="Departure Date" type="date" value={f.date} onChange={(v) => setF({ ...f, date: v })} testid="cruise-dep-date" />
              <IosField label="Departure Time" type="time" value={f.time} onChange={(v) => setF({ ...f, time: v })} testid="cruise-dep-time" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <IosField label="Arrival Date" type="date" value={f.arrive_date} onChange={(v) => setF({ ...f, arrive_date: v })} testid="cruise-arr-date" />
              <IosField label="Arrival Time" type="time" value={f.arrive_time} onChange={(v) => setF({ ...f, arrive_time: v })} testid="cruise-arr-time" />
            </div>
            <IosField label="Confirmation" value={f.ref || ""} onChange={(v) => setF({ ...f, ref: v })} testid="cruise-confirmation" />
            <IosField label="Total Cost" type="text" inputMode="decimal" value={f.total_cost || ""} onChange={(v) => setF({ ...f, total_cost: v })} placeholder="0.00" testid="cruise-cost" />

            <div className="pt-1">
              <div className="font-serif font-800 text-lg text-slate-900 mb-2">Starting Port</div>
              <div className="space-y-4">
                <IosField label="Port Name" value={f.from_place || ""} onChange={(v) => setF({ ...f, from_place: v })} placeholder="Miami · Port Everglades…" testid="cruise-start-port" />
                <IosField label="Port Address" value={f.start_address || ""} onChange={(v) => setF({ ...f, start_address: v })} placeholder="Street address" testid="cruise-start-addr" />
              </div>
            </div>

            <div className="pt-1">
              <div className="font-serif font-800 text-lg text-slate-900 mb-2">Ending Port</div>
              <label className="flex items-center justify-between py-2 border-b border-slate-200 mb-3">
                <span className="text-base text-slate-900">Same as Starting Port</span>
                <input type="checkbox" data-testid="cruise-same-toggle"
                  checked={f.end_same !== false}
                  onChange={(e) => setF({ ...f, end_same: e.target.checked })}
                  className="w-11 h-6 rounded-full appearance-none bg-slate-300 checked:bg-blue-500 relative cursor-pointer transition-colors before:absolute before:content-[''] before:w-5 before:h-5 before:rounded-full before:bg-white before:top-0.5 before:left-0.5 before:transition-transform checked:before:translate-x-5" />
              </label>
              {f.end_same === false && (
                <div className="space-y-4" data-testid="cruise-end-fields">
                  <IosField label="Port Name" value={f.to_place || ""} onChange={(v) => setF({ ...f, to_place: v })} placeholder="Different port" testid="cruise-end-port" />
                  <IosField label="Port Address" value={f.end_address || ""} onChange={(v) => setF({ ...f, end_address: v })} placeholder="Street address" testid="cruise-end-addr" />
                </div>
              )}
            </div>

            <div className="pt-1">
              <div className="font-serif font-800 text-lg text-slate-900 mb-2">Reservation Info</div>
              <div className="space-y-4">
                <IosField label="Cabin Type" value={f.cabin_type || ""} onChange={(v) => setF({ ...f, cabin_type: v })} placeholder="Balcony, interior, suite…" testid="cruise-cabin" />
                <IosField label="Cabin Number" value={f.cabin_number || ""} onChange={(v) => setF({ ...f, cabin_number: v })} placeholder="e.g. 8324" testid="cruise-cabin-num" />
                <IosField label="Guest Names" value={f.passenger || ""} onChange={(v) => setF({ ...f, passenger: v })} placeholder="Comma-separated" testid="cruise-guests" />
              </div>
            </div>
          </div>
        )}

        {/* iOS-native Directions form — Directions Type dropdown + route fields */}
        {m === "directions" && (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4" data-testid="form-directions">
            <label className="block">
              <div className="text-[13px] text-blue-600 font-600 mb-1">Directions Type</div>
              <select
                data-testid="dir-type"
                value={f.dir_type || "driving"}
                onChange={(e) => setF({ ...f, dir_type: e.target.value })}
                className="w-full bg-transparent text-blue-700 text-base py-1.5 border-0 border-b border-slate-300 focus:border-blue-500 focus:ring-0 outline-none">
                <option value="driving">Driving Directions</option>
                <option value="walking">Walking Directions</option>
                <option value="cycling">Cycling Directions</option>
                <option value="transit">Public Transit</option>
              </select>
            </label>
            <IosField label="Route Name" value={f.from_place} onChange={(v) => setF({ ...f, from_place: v })} placeholder="Airport to hotel" testid="dir-name" required />
            <IosPhotoBlock tickets={f.tickets} onPick={uploadTicket} busy={busy} testid="dir-upload" />
            <IosField label="Start Address" value={f.start_address || ""} onChange={(v) => setF({ ...f, start_address: v })} placeholder="Starting point" testid="dir-start" />
            <IosField label="End Address" value={f.to_place || ""} onChange={(v) => setF({ ...f, to_place: v })} placeholder="Destination" testid="dir-end" />
            <div className="grid grid-cols-2 gap-4">
              <IosField label="Date" type="date" value={f.date} onChange={(v) => setF({ ...f, date: v })} testid="dir-date" />
              <IosField label="Time" type="time" value={f.time} onChange={(v) => setF({ ...f, time: v })} testid="dir-time" />
            </div>
            <DirectionsField
              startAddress={f.start_address} destAddress={f.to_place}
              provider={f.map_provider} averageTime={f.average_time} routeLink={f.route_link}
              onChange={(d) => setF({ ...f, map_provider: d.provider, average_time: d.average_time, route_link: d.route_link })}
              testPrefix="dir-directions" />
          </div>
        )}

        {/* iOS-native Ferry / Boat Ride form — sectioned Departure / Arrival / Vehicle Info per user screenshot */}
        {m === "ferry" && (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-5" data-testid="form-ferry">
            <div>
              <div className="text-[10px] font-800 uppercase tracking-widest text-rose-600 mb-1">Ferry / Boat Ride</div>
              <IosField label="Carrier Name" value={f.carrier} onChange={(v) => setF({ ...f, carrier: v })} placeholder="Location · Name · Address" testid="ferry-carrier" required />
            </div>
            <IosPhotoBlock tickets={f.tickets} onPick={uploadTicket} busy={busy} testid="ferry-upload" />
            <div className="grid grid-cols-2 gap-4">
              <IosField label="Departure Date" type="date" value={f.date} onChange={(v) => setF({ ...f, date: v })} testid="ferry-dep-date" />
              <IosField label="Departure Time" type="time" value={f.time} onChange={(v) => setF({ ...f, time: v })} testid="ferry-dep-time" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <IosField label="Arrival Date" type="date" value={f.arrive_date} onChange={(v) => setF({ ...f, arrive_date: v })} testid="ferry-arr-date" />
              <IosField label="Arrival Time" type="time" value={f.arrive_time} onChange={(v) => setF({ ...f, arrive_time: v })} testid="ferry-arr-time" />
            </div>
            <IosField label="Confirmation" value={f.ref || ""} onChange={(v) => setF({ ...f, ref: v })} testid="ferry-confirmation" />
            <IosField label="Total Cost" type="text" inputMode="decimal" value={f.total_cost || ""} onChange={(v) => setF({ ...f, total_cost: v })} placeholder="0.00" testid="ferry-cost" />

            <div className="pt-1">
              <div className="font-serif font-800 text-lg text-slate-900 mb-2">Departure Location</div>
              <div className="space-y-4">
                <IosField label="Departure Location" value={f.from_place || ""} onChange={(v) => setF({ ...f, from_place: v })} placeholder="Port / terminal" testid="ferry-dep-loc" />
                <IosField label="Departure Address" value={f.start_address || ""} onChange={(v) => setF({ ...f, start_address: v })} placeholder="Street address" testid="ferry-dep-addr" />
              </div>
            </div>

            <div className="pt-1">
              <div className="font-serif font-800 text-lg text-slate-900 mb-2">Arrival Location</div>
              <div className="space-y-4">
                <IosField label="Arrival Location" value={f.to_place || ""} onChange={(v) => setF({ ...f, to_place: v })} placeholder="Port / terminal" testid="ferry-arr-loc" />
                <IosField label="Arrival Address" value={f.end_address || ""} onChange={(v) => setF({ ...f, end_address: v })} placeholder="Street address" testid="ferry-arr-addr" />
              </div>
            </div>

            <DirectionsField
              startAddress={f.start_address} destAddress={f.end_address}
              provider={f.map_provider} averageTime={f.average_time} routeLink={f.route_link}
              onChange={(d) => setF({ ...f, map_provider: d.provider, average_time: d.average_time, route_link: d.route_link })}
              testPrefix="ferry-directions" />

            <div className="pt-1">
              <div className="font-serif font-800 text-lg text-slate-900 mb-2">Vehicle Info</div>
              <div className="space-y-4">
                <IosField label="Vehicle Description" value={f.vehicle || ""} onChange={(v) => setF({ ...f, vehicle: v })} placeholder="Make · model · color" testid="ferry-vehicle" />
                <IosField label="License Plate" value={f.license_plate || ""} onChange={(v) => setF({ ...f, license_plate: v })} testid="ferry-plate" />
              </div>
            </div>
          </div>
        )}

        {/* iOS-native Map form — Map Name, Address, Date/Time, Upload Photo per user screenshot */}
        {m === "map" && (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4" data-testid="form-map">
            <IosField label="Map Name" value={f.from_place} onChange={(v) => setF({ ...f, from_place: v })} placeholder="Where is this map for?" testid="map-name" required />
            <IosField label="Address" value={f.to_place || ""} onChange={(v) => setF({ ...f, to_place: v })} placeholder="Street address" testid="map-address" />
            <div className="grid grid-cols-2 gap-4">
              <IosField label="Date" type="date" value={f.date} onChange={(v) => setF({ ...f, date: v })} testid="map-date" />
              <IosField label="Time" type="time" value={f.time} onChange={(v) => setF({ ...f, time: v })} testid="map-time" />
            </div>
            <IosPhotoBlock tickets={f.tickets} onPick={uploadTicket} busy={busy} testid="map-upload" />
          </div>
        )}

        {/* Legacy generic-field grid — only shown for modes that don't have a
            dedicated iOS-native form yet. */}
        {!["flight", "hotel", "activity", "meet_someone", "restaurant", "car_rental",
          "concert", "meeting", "cruise", "directions", "ferry", "map",
          "theater", "note", "parking", "rail", "tour", "transportation"].includes(m) && (
        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className={lbl}>{L(m, "ref", "Number / reference")}</label>
            <input value={f.ref || ""} onChange={set("ref")} data-testid="seg-ref" className={inp} /></div>
          <div><label className={lbl}>Carrier / operator / provider</label>
            <input value={f.carrier || ""} onChange={set("carrier")} data-testid="seg-carrier" className={inp} placeholder="Lufthansa, Amtrak, Uber, Marriott…" /></div>
          <div><label className={lbl}>{L(m, "from", "From")}</label>
            <input value={f.from_place || ""} onChange={set("from_place")} data-testid="seg-from" className={inp} /></div>
          {L(m, "to", "To") !== "" && (
            <div><label className={lbl}>{L(m, "to", "To")}</label>
              <input value={f.to_place || ""} onChange={set("to_place")} data-testid="seg-to" className={inp} /></div>)}
          <div><label className={lbl}>{L(m, "date", "Date")} / {L(m, "time", "time")}</label>
            <div className="flex gap-2">
              <input type="date" value={f.date || ""} onChange={set("date")} data-testid="seg-date" className={inp} />
              <input type="time" value={f.time || ""} onChange={set("time")} data-testid="seg-time" className={inp} />
            </div></div>
          <div><label className={lbl}>{L(m, "arrive_date", "Arrival date")} / {L(m, "arrive_time", "time")} <span className="normal-case">(optional)</span></label>
            <div className="flex gap-2">
              <input type="date" value={f.arrive_date || ""} onChange={set("arrive_date")} className={inp} />
              <input type="time" value={f.arrive_time || ""} onChange={set("arrive_time")} className={inp} />
            </div></div>
          {["train", "rail", "bus", "ferry"].includes(m) && (
            <div><label className={lbl}>Seat / terminal / gate</label>
              <div className="flex gap-2">
                <input value={f.seat || ""} onChange={set("seat")} data-testid="seg-seat" className={inp} placeholder="Seat" />
                <input value={f.terminal || ""} onChange={set("terminal")} className={inp} placeholder="Terminal" />
                <input value={f.gate || ""} onChange={set("gate")} data-testid="seg-gate" className={inp} placeholder="Gate" />
              </div></div>)}
          <div><label className={lbl}>{L(m, "passenger", "Passenger(s)")}</label>
            <input value={f.passenger || ""} onChange={set("passenger")} data-testid="seg-passenger" className={inp} /></div>
          <div><label className={lbl}>Booking ref / PNR</label>
            <input value={f.booking_ref || ""} onChange={set("booking_ref")} data-testid="seg-pnr" className={inp} /></div>
          <div><label className={lbl}>Ticket number</label>
            <input value={f.ticket_number || ""} onChange={set("ticket_number")} className={inp} /></div>
          {["car", "car_rental", "reservation"].includes(m) && (
            <div><label className={lbl}>Free-cancellation deadline <span className="normal-case">(optional)</span></label>
              <div className="flex gap-2">
                <input type="date" value={(f.cancel_deadline || "").slice(0, 10)}
                  onChange={(e) => setF({ ...f, cancel_deadline: e.target.value ? `${e.target.value}T${(f.cancel_deadline || "").slice(11, 16) || "23:59"}` : "" })}
                  data-testid="seg-cancel-date" className={inp} />
                <input type="time" value={(f.cancel_deadline || "").slice(11, 16)}
                  onChange={(e) => setF({ ...f, cancel_deadline: `${(f.cancel_deadline || "").slice(0, 10) || ""}T${e.target.value}` })}
                  data-testid="seg-cancel-time" className={inp} />
              </div></div>)}
        </div>
        )}

        {/* Universal "Upload Photo" — every plan type gets it, per project standard. */}
        {!["flight", "hotel", "activity", "meet_someone", "restaurant", "car_rental",
          "concert", "meeting", "cruise", "directions", "ferry", "map",
          "theater", "note", "parking", "rail", "tour", "transportation"].includes(m) && (
          <>
            <div><label className={lbl}>Notes</label>
              <input value={f.notes || ""} onChange={set("notes")} data-testid="seg-notes" className={inp} placeholder="Collect baggage, change terminal, wheelchair assistance…" /></div>
            <IosPhotoBlock tickets={f.tickets} onPick={uploadTicket} busy={busy} testid={`upload-${m}`} />
          </>
        )}

        <div className="flex flex-wrap gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-zinc-300">Cancel</button>
          {m === "flight" && (
            <button
              onClick={async () => {
                // Save this leg then reopen the Flight form with a blank leg so the
                // user can add the next connecting flight without navigating.
                onSave({ ...f, id: f.id, _add_another: true });
              }}
              data-testid="seg-save-add-another"
              className="px-4 py-2 text-sm font-700 border border-primary text-primary hover:bg-blue-50">
              Save & add another leg
            </button>
          )}
          <button onClick={submit} data-testid="seg-save" className="px-5 py-2 text-sm font-bold bg-primary text-white hover:bg-blue-700">
            <Ticket className="w-4 h-4 inline mr-1" /> Save {m === "flight" ? "flight" : "leg"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
